"use client";
import { jsx } from "react/jsx-runtime";
import { useMemo, useEffect, useState, useContext } from "react";
import cx from "classnames";
import { FormFieldProvider } from "@ingka/form-field";
import InputField from "@ingka/input-field";
import { SliderContext } from "./helpers/SliderContext.mjs";
import {
  elx,
  updateAndClone,
  evaluateInputSize,
  inputSizeClass
} from "@ingka/slider-shared";
const SliderInputControls = (props) => {
  const {
    labels = [],
    onInput,
    onFocus,
    onBlur,
    formattedEdit = false,
    onValueEntered,
    ariaFormat,
    type: forcedInputType,
    ...otherProps
  } = props;
  const ctx = useContext(SliderContext);
  const [localValues, setLocalValues] = useState(
    ctx.values.map(ctx.format.to)
  );
  useEffect(() => {
    setLocalValues(ctx.values.map(ctx.format.to));
  }, [...ctx.values, ctx.format.to]);
  const requestedInputSize = otherProps.inputSize || "auto";
  delete otherProps.inputSize;
  const calculatedInputSize = useMemo(() => {
    if (requestedInputSize === "auto") {
      return evaluateInputSize(
        ctx.range,
        ctx.format,
        ctx.step,
        ctx.prefixLabel,
        ctx.suffixLabel
      );
    }
    return requestedInputSize;
  }, [
    requestedInputSize,
    ctx.prefixLabel,
    ctx.suffixLabel,
    ctx.step,
    ctx.range.min,
    ctx.range.max,
    ctx.format.to
  ]);
  const inputClasses = cx(
    `${ctx.baseClass}__input`,
    inputSizeClass[calculatedInputSize] && `${ctx.baseClass}__input${inputSizeClass[calculatedInputSize]}`
  );
  const focusHandler = (e) => {
    const inputID = parseInt(e.target.dataset.index ?? "0", 10);
    const rawValue = e.target.value;
    const focused = e.type === "focus";
    if (!focused) {
      const result = updateAndClone(
        ctx.values,
        inputID,
        ctx.format.from(rawValue)
      );
      const formattedValues = ctx.values.map(ctx.format.to);
      setLocalValues(formattedValues);
      ctx.onValueEntered(result);
      onValueEntered?.(result);
    } else if (!formattedEdit) {
      setLocalValues(
        updateAndClone(
          localValues,
          inputID,
          ctx.format.from(rawValue).toString()
        )
      );
    }
  };
  function updateValueFromEvent(ev) {
    const {
      target: {
        dataset: { index },
        value
      }
    } = ev;
    setLocalValues((v) => updateAndClone(v, index || 0, value));
  }
  function keydownHandler(ev) {
    if (ev.key === "Enter")
      focusHandler(ev);
  }
  return (
    /**
     * Send valid and shouldValidate to both input fields, but only render one "FormField"
     */
    /* @__PURE__ */ jsx("div", { className: `${ctx.baseClass}__input-controls`, children: /* @__PURE__ */ jsx("div", { className: `${ctx.baseClass}__input-fields`, children: ctx.values.map((value, index) => {
      const inputID = `${ctx.baseId}__rangeInput${index}`;
      const labelledByIDs = `${ctx.labelID} ${inputID}`;
      return /* @__PURE__ */ jsx(
        FormFieldProvider,
        {
          shouldValidate: !!ctx.validationMessages[index],
          valid: !ctx.validationMessages[index] ? "success" : "error",
          children: /* @__PURE__ */ jsx(
            InputField,
            {
              "aria-label": (ariaFormat || ctx.ariaFormat)?.to(value).toString(),
              "aria-labelledby": labelledByIDs,
              ...otherProps,
              disabled: ctx.disabled,
              onKeyDown: elx(otherProps.onKeyDown, keydownHandler),
              onFocus: elx(onFocus, focusHandler),
              onBlur: elx(onBlur, focusHandler),
              onInput,
              onChange: updateValueFromEvent,
              id: inputID,
              "data-index": index,
              type: forcedInputType || formattedEdit ? "text" : "number",
              prefix: ctx.prefix,
              prefixLabel: ctx.prefixLabel,
              label: labels[index] || void 0,
              suffixLabel: ctx.suffixLabel,
              className: inputClasses,
              describedById: ctx.formFieldID,
              value: localValues[index]
            }
          )
        },
        index
      );
    }) }) })
  );
};
var SliderInputControls_default = SliderInputControls;
export {
  SliderInputControls_default as default
};
